/**
 * Settings → AI (superadmin).
 *
 * One switch with one status line (env switch + setting), then the providers
 * as an ORDERED list — row 1 answers first, row 2 takes over if it fails.
 * Order changes by "Make primary" on the backup row; health comes from the
 * most recent result, never from key presence.
 *
 * Keys are never shown, entered or stored here — they live in the Vercel
 * environment (ANTHROPIC_SALES_APP_BACKEND_KEY, OPENAI_API_KEY). Spec §7.2.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Switch } from '@/components/ui/switch';
import {
  getAiSettings,
  setAiModelOverride,
  testAiProvider,
  updateAiSettings,
  type AiProviderId,
  type AiSettingsResponse,
} from '@/lib/aiSettingsApi';
import { assistanceStatus, routingOrder, summarizeFailure } from '@/lib/aiHealth';
import { AiProviderRow, type InlineTestResult } from './AiProviderRow';

type ProviderSnapshot = AiSettingsResponse['providers'][number];

/** The stored-health fingerprint an inline Test result was taken against. */
const healthStamp = (p: ProviderSnapshot | undefined) => `${p?.last_success_at ?? ''}|${p?.last_error_at ?? ''}`;

/** An inline Test result, valid only while the stored health it was read against is unchanged. */
interface StampedResult { result: InlineTestResult; stamp: string }

export function AiSettings() {
  const [data, setData] = useState<AiSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Keyed by provider. A result is dropped (not shown) once a newer snapshot
  // records a later success or error for that provider: live traffic has
  // spoken since the Test, so the stored health is the fresher truth.
  const [results, setResults] = useState<Partial<Record<AiProviderId, StampedResult>>>({});

  const load = useCallback(async () => {
    try {
      setData(await getAiSettings());
    } catch (err) {
      toast.error(`Could not load AI settings: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const run = async (key: string, fn: () => Promise<AiSettingsResponse | void>, done?: string) => {
    setBusy(key);
    try {
      const next = await fn();
      if (next) setData(next);
      if (next && next.audit_failed) {
        toast.error('The change was saved, but its audit record could not be written. Please tell an engineer.');
      } else if (done) {
        toast.success(done);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!data) return <div className="p-6 text-sm text-muted-foreground">AI settings are available to superadmins.</div>;

  const primary = data.providers.find((p) => p.id === data.primary);
  const backup = data.providers.find((p) => p.id === data.backup);
  // What the router will actually use: providers in the order that have a key.
  const serving = [primary, backup].filter((p): p is NonNullable<typeof p> => Boolean(p?.configured));
  // The router skips providers whose breaker is open; with none left it runs nothing.
  const runnable = serving.filter((p) => !p.breaker_open_until);
  const paused = serving.filter((p) => p.breaker_open_until);
  const servingLine = runnable.length === 0
    ? 'Not running'
    : `Served by ${runnable[0].label}${runnable[1] ? `, ${runnable[1].label} as backup` : ''}${paused.map((p) => `, ${p.label} paused`).join('')}`;
  const status = assistanceStatus({ ...data, any_configured: serving.length > 0, any_runnable: runnable.length > 0 });
  const rows = routingOrder(data.providers, data.primary, data.backup);

  const inlineResult = (p: ProviderSnapshot) => {
    const r = results[p.id];
    return r && r.stamp === healthStamp(p) ? r.result : null;
  };

  const test = (id: AiProviderId, label: string) => void run(id, async () => {
    setResults((prev) => ({ ...prev, [id]: undefined }));
    const r = await testAiProvider(id);
    // Stamped against the snapshot on screen, so it still shows if the refresh fails.
    setResults((prev) => ({ ...prev, [id]: { result: r, stamp: healthStamp(data.providers.find((p) => p.id === id)) } }));
    if (!r.ok) {
      const summary = summarizeFailure({ kind: r.kind, status: r.status, type: r.error_type, message: r.error_message }, label);
      toast.error(summary.includes(label) ? summary : `${label}: ${summary}`);
    }
    if (r.audit_failed) toast.error('The Test ran, but its audit record could not be written.');
    const next = await getAiSettings();
    // The refresh may carry this Test's own outcome: re-stamp against it.
    setResults((prev) => (prev[id] ? { ...prev, [id]: { result: r, stamp: healthStamp(next?.providers.find((p) => p.id === id)) } } : prev));
    return next;
  });

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">
      <div className="max-w-3xl space-y-6">
        <header className="space-y-2">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">AI</h2>
          <p className="text-sm text-muted-foreground">
            Suggests unit assignments from pasted text. Nothing is applied until someone accepts it.
          </p>
          <details className="text-xs">
            <summary className="cursor-pointer select-none text-muted-foreground hover:text-foreground">What gets sent</summary>
            <p className="mt-2 max-w-2xl rounded border border-border p-3 text-muted-foreground">{data.privacy_note}</p>
          </details>
        </header>

        <section aria-label="AI assistance" className="rounded-md border border-border">
          <div className="flex items-start justify-between gap-4 p-4">
            <div className="min-w-0 space-y-1">
              <div className="text-sm font-medium text-foreground">AI assistance</div>
              <p
                data-testid="ai-status"
                className={`text-xs ${status.state === 'on' ? 'text-[#8FB560]' : 'text-muted-foreground'}`}
              >
                {status.text}
              </p>
              {status.hint && <p data-testid="ai-status-hint" className="text-xs text-muted-foreground">{status.hint}</p>}
            </div>
            <Switch
              // The switch shows the SAVED setting and stays editable even while
              // the env blocks assistance: saving it calls no provider, and a
              // hidden "on" would start assistance the moment the env allows it.
              checked={data.enabled}
              disabled={busy !== null}
              onCheckedChange={(v) => run('enabled', () => updateAiSettings({ enabled: v }), v ? 'AI assistance on.' : 'AI assistance off.')}
              aria-label="AI assistance"
            />
          </div>
          <ul className="divide-y divide-border border-t border-border text-xs">
            {data.features.map((f) => (
              <li key={f.key} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                <span className="text-foreground">{f.label}</span>
                <span className="text-muted-foreground">
                  {status.state === 'on' ? servingLine : 'Not running'}
                </span>
              </li>
            ))}
          </ul>
          {!data.store_ok && (
            <p className="border-t border-border px-4 py-2 text-xs text-[#E0556C]">
              Shared state (Redis) is unavailable, so AI assistance is off until it recovers.
            </p>
          )}
        </section>

        <section aria-label="Providers" className="space-y-2">
          <div>
            <h3 className="text-sm font-medium text-foreground">Providers</h3>
            <p className="text-xs text-muted-foreground">Requests go to the primary; the backup takes over if it fails.</p>
          </div>
          <ol className="divide-y divide-border rounded-md border border-border">
            {rows.map(({ provider: p, role }) => (
              <AiProviderRow
                key={p.id}
                p={p}
                role={role}
                result={inlineResult(p)}
                busy={busy === p.id}
                // One shared busy slot: lock every row while ANY action runs,
                // so actions cannot overlap and finish out of order.
                locked={data.env_killed || (busy !== null && busy !== p.id)}
                // Order/backup are settings only (no provider call): usable
                // even under the kill switch, so failover can be prepared.
                routingLocked={busy !== null}
                onTest={() => test(p.id, p.label)}
                onMakePrimary={() => void run('order', () => updateAiSettings({ primary: p.id, backup: data.primary }), `${p.label} is now primary.`)}
                onUseAsBackup={() => void run('order', () => updateAiSettings({ primary: data.primary, backup: p.id }), `${p.label} is now the backup.`)}
                onRemoveBackup={() => void run('order', () => updateAiSettings({ primary: data.primary, backup: null }), 'No backup — failover is off.')}
                onModel={(model) => void run(p.id, async () => {
                  const next = await setAiModelOverride(p.id, model);
                  // A Test result was for the previous model; the snapshot now speaks.
                  // Saving a model requires its probe to pass, so a new model is
                  // known-good; a reset has no probe, so its result is unknown.
                  setResults((prev) => ({
                    ...prev,
                    [p.id]: model ? { result: { ok: true }, stamp: healthStamp(next?.providers.find((x) => x.id === p.id)) } : undefined,
                  }));
                  return next;
                },
                  model ? `${p.label} now uses ${model}.` : `${p.label} is back on its default model.`)}
              />
            ))}
          </ol>
          {!(backup?.configured) && (
            <p className="text-xs text-muted-foreground">No working backup — if the primary fails, assistance is unavailable.</p>
          )}
        </section>
      </div>
    </div>
  );
}
